/* global _TL_ */
import Backbone from 'backbone';
import _ from 'underscore';
import Promise from 'bluebird';
import React from 'react';
import ReactDOM from 'react-dom';
import config from '@bingads-webui-component-legacy/config/index';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { saveAds } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { AdEditor, imagePlacementsToImage, getVerifiedTrackingSettings } from '@bingads-webui-campaign-react/xandr-display-ads-campaign-shared-components';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ImageRepository } from '@bingads-webui-campaign-react/image-experience';
import { constants, newImageFromMTObject } from '@bingads-webui-campaign-react/image-experience-model';
import { getAutoCroppedImages } from '@bingads-webui-campaign/image-experience-utils';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';

export default class View extends Backbone.View {
  initialize({
    scope,
    editMode = false,
    model = {},
    showInline = false,
    instrumentation,
    perfMarker,
    permissions,
    activityLogger,
    campaignId,
    adGroupId,
    serverContext,
    preferenceService,
  }) {
    this.aid = _.get(serverContext, ['CurrentAccount', 'Id']);
    this.cid = _.get(serverContext, ['CurrentCustomer', 'Id']);
    this.advertiserCustomerId = _.get(serverContext, ['CurrentAccount', 'AdvertiserCustomerID']);
    this.scope = scope;
    this.editMode = editMode;
    this.showInline = showInline;
    this.perfMarker = perfMarker;
    this.instrumentation = instrumentation;
    this.model = model;
    this.apiErrors = [];
    this.campaignId = campaignId || this.scope().campaignId;
    this.adGroupId = adGroupId || this.scope().adGroupId;
    this.activityLogger = activityLogger;
    this.resetFields = false;
    this.permissions = permissions;
    this.serverErrors = null;
    this.serverContext = serverContext;
    this.preferenceService = preferenceService;
    this.verifiedTrackingSetting = this.editMode ? getVerifiedTrackingSettings(model) : {};
    this.savedAdModel = {
      isValid: true,
      isDirty: false,
      data: [{
        isDirty: false,
        isValid: true,
        data: {
          TrackingUrlTemplate: model && model.TrackingUrlTemplate,
          FinalUrlSuffix: model && model.FinalUrlSuffix,
          UrlCustomParameters: model && model.UrlCustomParameters,
          ImpressionTrackingUrls: this.editMode ? model && model.ImpressionTrackingUrls : [],
          FinalUrls: this.editMode ? model && model.FinalUrls : [],
          image: null,
          ...this.verifiedTrackingSetting,
        },
        uid: Math.random(),
        Id: this.editMode ? model.Id : null,
      }],
      currentPageIndex: 1,
    };

    this.scenario = 'Edit-DisplayAds';

    this.oDataConfig = {
      odataRootUrl: config.get('CampaignAzureDomain'),
      odataPath: config.get('odata.url') + config.get('odata.path'),
      odataToken: config.get('odata.token'),
      oDataToken: config.get('odata.token'),
      oDataPath: config.get('odata.url') + config.get('odata.path'),
    };

    this.imageRepository = new ImageRepository(this.advertiserCustomerId, this.aid, this.oDataConfig);
    if (model && model.Images && model.Images.length > 0) {
      const imageAsset = model.Images[0].Asset;

      this.isLoading = true;

      this.imagePromise = this.imageRepository.getImageByIdAsync(imageAsset.IdString)
        .then((imgObjMT) => {
          const imageObject = newImageFromMTObject(imgObjMT, constants.imageSources.Existing);
          const croppedImagePlacements = getAutoCroppedImages({
            originalImageToCrop: imageObject,
            isMultimediaAd: false,
            isMultimediaAdinMSANEnabled: false,
            imageOption: constants.defaultImageOption,
            aspectRatioOption: constants.defaultAspectRatioOption,
            isDisplayAds: true,
          });
          const imageData = imagePlacementsToImage({crops: croppedImagePlacements,
            selected: {
              TargetWidth: imageAsset.TargetWidth,
              TargetHeight: imageAsset.TargetHeight,
              CropWidth: imageAsset.CropWidth,
              CropHeight: imageAsset.CropHeight,
              CropX: imageAsset.CropX,
              CropY: imageAsset.CropY,
            }});

          this.savedAdModel.data[0].data = {...this.savedAdModel.data[0].data, ...imageData.data};
          this.isLoading = false;
          this.render();
        });
    }
  }

  onAdsValidation(result) {
    this.savedAdModel = { ...result };
    this.render();
  }

  validate() {
    return this.savedAdModel.isValid;
  }

  toObject() {
    const params = { Images: this.savedAdModel.data[0].data.image, FinalUrls: this.savedAdModel.data[0].data.FinalUrls };

    return params;
  }

  clearApiErrors() {
    this.apiErrors = [];
  }

  saveAdAsync(updateGrid) {
    if (!this.validate()) {
      return Promise.resolve(false);
    }

    return saveAds({
      ads: this.savedAdModel,
      campaign: { Id: this.campaignId },
      adGroup: { Id: this.adGroupId },
      activity: this.activityLogger,
    }, { scenario: this.scenario, i18n: this.options.newI18n, activityLogger: this.activityLogger }).then((result) => {
      if (result && result.ads && result.ads.data) {
        return updateGrid(result.ads.data[0], result.ads.data[0].Id, 'Responsive');
      }
      return result.ads.data[0];
    }).catch((result) => {
      this.serverErrors = result.serverErrors;
      this.render();
    });
  }

  clearFields() {
    this.editMode = false;
    this.model = {};
    this.resetFields = !this.resetFields;
    this.savedAdModel = {
      isValid: true,
      isDirty: false,
      data: [{
        isDirty: false,
        isValid: true,
        data: {
          FinalUrls: [],
          image: null,
        },
        Id: null,
      }],
      currentPageIndex: 1,
    };

    this.render();
  }

  remove() {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }

  saveAndCreateAnotherAsync(updateGrid) {
    if (!this.validate()) {
      return Promise.resolve(false);
    }

    return saveAds({
      ads: this.savedAdModel,
      campaign: { Id: this.campaignId },
      adGroup: { Id: this.adGroupId },
    }, { scenario: this.scenario, i18n: this.options.newI18n, activityLogger: this.activityLogger })
      .then((result) => {
        if (result && result.ads && result.ads.data) {
          this.clearFields();
          this.render();
          return updateGrid(result.ads.data[0], result.ads.data[0].Id, 'Responsive');
        }
        return result.ads.data[0];
      }).catch((result) => {
        this.serverErrors = result.serverErrors;
        this.render();
      });
  }

  render() {
    const scenarioName = 'XandrDisplayAdsAdEditing';
    const activityName = 'Ad';

    ReactDOM.render(
      <I18nContext.Provider value={this.options.newI18n}>
        <InstrumentationContext.Provider value={this.instrumentation}>
          <Scenario name={scenarioName}>
            <Activity name={activityName}>
              <ServerContext.Provider value={this.serverContext}>
                <PreferenceContext.Provider value={this.preferenceService}>
                  <UiBlocker
                    blocking={this.isLoading}
                    loadingText={this.options.newI18n.getString(_TL_('Loading...'))}
                    classes={{
                      blockUiContainer: 'blocker-global',
                      blockerIndicator: 'blocker-indicator',
                    }}
                  >
                    { this.isLoading ?
                      <div style={{height: '412px'}}></div> :
                      <AdEditor
                        aid={_.result(config.get('CurrentAccount'), 'Id')}
                        cid={_.result(config.get('CurrentCustomer'), 'Id')}
                        campaignId={this.campaignId}
                        i18n={this.options.newI18n}
                        perfMarker={this.perfMarker}
                        permissions={this.permissions}
                        onAdsValidation={this.onAdsValidation.bind(this)}
                        oDataConfig={this.oDataConfig}
                        resetFields={this.resetFields}
                        adsModel={this.savedAdModel}
                        serverErrors={this.serverErrors}
                        isLoadingImage={this.isLoading}
                      />
                    }
                  </UiBlocker>
                </PreferenceContext.Provider>
              </ServerContext.Provider>
            </Activity>
          </Scenario>
        </InstrumentationContext.Provider>
      </I18nContext.Provider>
      ,
      this.el,
    );

    return this;

  }
}
